import {Page as WakeletPage} from "@wakelet/ui-kit";
import React, {FC, ReactElement, ReactNode} from "react";
import {Footer} from "../components/footer/Footer";
import {OAuthStore} from "../components/auth/store";
import {getReturnUrl, getWakeletUrl} from "../env/env";
import {useAuth} from "../components/auth/AuthContext";
import {Navigate} from "react-router-dom";
import {useSwitchAccount} from "../components/switch-account/switch-account-store";
import {ErrorBoundary} from "../components/errors/ErrorBoundary";
import {FullPageSpinner} from "../components/loading/Spinner";
import {IconWakeletWhiteW} from "../components/icons/icons";
import {translateKey} from "../components/language/translate";
import {requiresVerification} from "../components/auth/types";

type PageVariant = "primary" | "secondary";

type Props = {
  children?: React.ReactNode,
  variant?: PageVariant,
  showHeader?: boolean,
  stickContentToTop?: boolean,
  returnTo?: string,
  allowAuthUnverified?: boolean,
}

export const Page: FC<Props> = ({children, variant, allowAuthUnverified = false}) => {
  return requireUnauth(requireSession(<PageWithLayout variant={variant}>{children}</PageWithLayout>), {allowAuthenticatedUnverified: allowAuthUnverified});
};

export const PageWithLayout: FC<Props> = (
  {children, variant = "primary", showHeader = false, stickContentToTop = false, returnTo}) => {
  const bgColour = variant === "primary" ? "bg-white" : "bg-gray-100";
  const contentClass = stickContentToTop ? "page-content-align-top" : "page-content";
  return <WakeletPage>
    <WakeletPage.Frame>
      {showHeader && <WakeletPage.FrameHeader primarySlot={wakeletLogo(returnTo)}/>}
      <WakeletPage.FrameBody>
        <div className={`flex relative flex-col ${contentClass} ${bgColour}`}>
          <ErrorBoundary>
            {children}
          </ErrorBoundary>
        </div>
      </WakeletPage.FrameBody>
      <WakeletPage.FrameFooter>
        <Footer/>
      </WakeletPage.FrameFooter>
    </WakeletPage.Frame>
  </WakeletPage>;
};

export const handleAuthError = (elem: ReactElement | null): ReactElement | null => {
  const {error} = useAuth();
  if (error) {
    console.error("error from auth context, redirecting to /error");
    return <Navigate to="/error"/>
  }
  return elem;
};

export type RequireUnauthOptions = {
  // When true, do NOT redirect authenticated-but-unverified users to /verify-email.
  allowAuthenticatedUnverified?: boolean;
};

export const requireUnauth = (elem: ReactElement | null, opts?: RequireUnauthOptions): ReactElement | null => {
  const {user, loading, error} = useAuth();
  const {isSwitching} = useSwitchAccount();
  const allowUnverified = !!opts?.allowAuthenticatedUnverified;
  if (loading) {
    console.info("loading");
    return <FullPageSpinner/>
  }
  if (error) {
    console.error("error from auth context, redirecting to /error");
    return <Navigate to="/error"/>
  }
  if (user && !isSwitching) {
    if (!requiresVerification(user)) {
        console.info("user is authenticated and does not require further verification, redirecting to /authenticated");
        return <Navigate to="/authenticated"/>
    }
    if (!allowUnverified) {
      console.info("user is authenticated and unverified, redirecting to /verify-email");
      return <Navigate to="/verify-email"/>
    }
  }
  return elem;
};

export const requireSession = (elem: ReactElement | null): ReactElement | null => {
  const sessionId = OAuthStore.getSession();
  if (!sessionId) {
    const wakeletUrl = getWakeletUrl();
    console.info(`no session, redirecting to: ${wakeletUrl}`);
    window.location.assign(wakeletUrl);
    return null;
  }
  return elem
}

export const PageWithoutFooter: FC<Props> = ({children}) => {
  return handleAuthError(
    <WakeletPage>
      <WakeletPage.Frame>
        <WakeletPage.FrameBody>
          <div className="px-5">{children}</div>
        </WakeletPage.FrameBody>
      </WakeletPage.Frame>
    </WakeletPage>
  )
};

const wakeletLogo = (returnTo?: string): ReactNode =>
  <a href={getReturnUrl(returnTo)} className="ml-1 rounded-sm"
     title={translateKey("common:links.home")}
     aria-labelledby={translateKey("common:links.home")}
  >
    <IconWakeletWhiteW size={50}/>
  </a>;
